import { useState, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { GridValidRowModel, GridCellParams } from "@mui/x-data-grid";
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import { getLoanTableColumns } from "../../../data/FormColumns/Fleet/loanTableColumns";
import LoanUnitsDrawer from "../LoanUnitsDrawer/LoanUnitsDrawer";
import PersistedDataGrid from "../../PersistedDataGrid/PersistedDataGrid";

export interface LoanBalanceDto {
  id: string;
  loanId: string;
  loanNumber: string;
  date: string;
  amount: number;
}

export const useUpdateLoanBalance = (selectedDate: string) => {
  const { token } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (dto: LoanBalanceDto) => {
      await axios.put(`${apiVariable}/updateLoanBalance`, dto, {
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["loans", selectedDate] });
    },
  });
};

export const fetchLoans = async (
  token: string | null | undefined,
  date: string,
  areActive: boolean,
) => {
  const { data } = await axios.get(`${apiVariable}/getAllLoans`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { date, areActive },
  });
  return data;
};

const LoanTable = () => {
  const { token } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [areActive, setAreActive] = useState(true);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState<{
    id: string;
    loanNumber: string;
  } | null>(null);

  const mutation = useUpdateLoanBalance(selectedDate);
  const processRowUpdate = async <R extends GridValidRowModel>(newRow: R) => {
    await mutation.mutateAsync({
      id: newRow.loanBalanceId,
      loanId: newRow.id,
      loanNumber: newRow.loanNumber,
      date: selectedDate,
      amount: Number(newRow.verifiedRemainingBalance),
    });

    return newRow;
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["loans", selectedDate, areActive],
    queryFn: () => fetchLoans(token, selectedDate, areActive),
    enabled: !!token,
  });

  const columns = useMemo(
    () => getLoanTableColumns(selectedDate),
    [selectedDate],
  );

  const filteredLoans = useMemo(() => {
    if (!data) return [];
    return data.filter((loan: any) =>
      [loan.loanFlag, loan.lenderName, loan.loanNumber].some((field) =>
        field?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    );
  }, [data, searchTerm]);

  const handleCellClick = (
    params: GridCellParams,
    _event: React.MouseEvent,
  ) => {
    if (
      params.field === "verifiedRemainingBalance" ||
      params.field === "actions" ||
      params.field === "delete"
    )
      return;

    setSelectedLoan(params.row);
    setDrawerOpen(true);
  };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAreActive(event.target.checked);
  };

  return (
    <Box className="incidents-table-container" height={900}>
      <Box
        className="table-controls"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            label="Select Date"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={areActive}
                onChange={handleSwitchChange}
                color="primary"
              />
            }
            label={`${areActive ? "Active" : "Inactive"} Loans`}
          />
        </Box>
        <TextField
          label="Search"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by Flag, Lender, Loan #..."
          sx={{ maxWidth: "100%", width: 400 }}
        />
      </Box>
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center">
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Typography color="error">Error: {error.message}</Typography>
      ) : (
        <PersistedDataGrid
          tableName="loans"
          rows={filteredLoans}
          columns={columns}
          onCellClick={handleCellClick}
          processRowUpdate={processRowUpdate}
          disableRowSelectionOnClick
          pagination
          sx={{ height: 700 }}
        />
      )}

      <LoanUnitsDrawer
        loanId={selectedLoan?.id}
        loanNumber={selectedLoan?.loanNumber}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
    </Box>
  );
};

export default LoanTable;
